import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { AdditionalOption } from './types';
import { cn } from '@/lib/utils';
import { 
  colors, 
  shadows, 
  borders, 
  spacing 
} from '@/components/business/design-system/businessDesignTokens';
import { AddOptionModalHeader } from './components/AddOptionModalHeader';
import { OptionBasicForm } from './components/OptionBasicForm';
import { DropdownChoicesForm } from './components/DropdownChoicesForm';
import { 
  OptionFormData, 
  getDefaultFormData, 
  validateAndSubmitOption 
} from './utils/optionValidation';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { X, Plus } from 'lucide-react';
import { typography } from '@/components/business/design-system/businessDesignTokens';

interface AddOptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddOption: (option: AdditionalOption) => void;
}

export function AddOptionModal({
  isOpen,
  onClose,
  onAddOption
}: AddOptionModalProps) {
  const [formData, setFormData] = useState<OptionFormData>(getDefaultFormData());

  const handleSubmit = () => {
    const success = validateAndSubmitOption(formData, onAddOption, () => {
      setFormData(getDefaultFormData());
      onClose();
    });
  };

  const updateFormData = (updates: Partial<OptionFormData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  const handleClose = () => {
    setFormData(getDefaultFormData());
    onClose();
  };

  // Legacy functions for the form components that haven't been fully refactored yet
  const updateChoiceValue = (index: number, value: string) => {
    const updatedChoices = [...formData.options];
    updatedChoices[index] = { ...updatedChoices[index], value };
    updateFormData({ options: updatedChoices });
  };

  const addChoice = () => {
    const newChoices = [
      ...formData.options,
      { value: `Option ${formData.options.length + 1}`, pricing: [] }
    ];
    updateFormData({ options: newChoices });
  };

  const removeChoice = (index: number) => {
    if (formData.options.length > 2) {
      const newChoices = formData.options.filter((_, i) => i !== index);
      updateFormData({ options: newChoices });
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[70] flex items-center justify-center p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className={cn(
            "bg-white rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto",
            shadows.level2.className,
            borders.container
          )}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={spacing.cardPadding}>
            <AddOptionModalHeader onClose={handleClose} />
            
            <div className={spacing.elementGap}>
              {/* Option Name */}
              <div>
                <Label htmlFor="option-label" className={cn(typography.body, "font-medium", `text-${colors.neutral[700]}`)}>
                  Option Name
                </Label>
                <Input
                  id="option-label"
                  value={formData.label}
                  onChange={(e) => updateFormData({ label: e.target.value })}
                  placeholder="e.g., Meal Package, Activity Level"
                  className={cn(
                    "mt-2",
                    borders.interactive,
                    borders.focus.primary,
                    shadows.simple.sm
                  )}
                />
              </div>

              {/* Option Type */}
              <div>
                <Label htmlFor="option-type" className={cn(typography.body, "font-medium", `text-${colors.neutral[700]}`)}>
                  Option Type
                </Label>
                <Select 
                  value={formData.type} 
                  onValueChange={(value: 'select' | 'checkbox' | 'text') => 
                    updateFormData({ type: value })
                  }
                >
                  <SelectTrigger className={cn(
                    "mt-2",
                    borders.interactive,
                    borders.focus.primary,
                    shadows.simple.sm
                  )}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={cn(
                    shadows.level1.className,
                    borders.container
                  )}>
                    <SelectItem value="select">Dropdown (Multiple choices)</SelectItem>
                    <SelectItem value="checkbox">Yes/No Choice</SelectItem>
                    <SelectItem value="text">Text Input</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Dropdown Choices */}
              {formData.type === 'select' && (
                <div>
                  <Label className={cn(typography.body, "font-medium", `text-${colors.neutral[700]}`)}>
                    Choices
                  </Label>
                  <div className="mt-2 space-y-3">
                    {formData.options.map((choice, idx) => (
                      <div key={idx} className="flex gap-2 items-center">
                        <Input
                          value={choice.value}
                          onChange={(e) => updateChoiceValue(idx, e.target.value)}
                          placeholder={`Choice ${idx + 1}`}
                          className={cn(
                            borders.interactive,
                            borders.focus.primary,
                            shadows.simple.sm
                          )}
                        />
                        {formData.options.length > 2 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeChoice(idx)}
                            className="flex-shrink-0 hover:bg-red-50 hover:text-red-600"
                            aria-label={`Remove choice ${idx + 1}`}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={addChoice}
                      className={cn(
                        "w-full",
                        borders.interactive,
                        shadows.simple.sm,
                        "hover:shadow-md transition-shadow"
                      )}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Choice
                    </Button>
                  </div>
                </div>
              )}

              {/* Required Field Toggle */}
              <div className={cn(
                "flex items-center justify-between p-4 rounded-lg border",
                `bg-${colors.neutral[50]}`,
                `border-${colors.neutral[200]}`,
                shadows.simple.sm
              )}>
                <div className="flex items-center gap-3">
                  <Switch
                    checked={formData.required}
                    onCheckedChange={(checked) => updateFormData({ required: checked })}
                    className="data-[state=checked]:bg-primary"
                  />
                  <div>
                    <Label className={cn(typography.body, "font-medium", `text-${colors.neutral[700]}`)}>
                      Required field
                    </Label>
                    <p className={cn(typography.caption, `text-${colors.neutral[600]}`)}>
                      Users must complete this option
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-slate-200/60">
              <Button
                variant="outline"
                onClick={handleClose}
                className={cn(
                  borders.interactive,
                  shadows.simple.sm,
                  "hover:shadow-md transition-shadow"
                )}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                className={cn(
                  `bg-${colors.success.main} hover:bg-${colors.success.hover}`,
                  shadows.simple.md,
                  "hover:shadow-lg transition-shadow px-6"
                )}
              >
                Add Option
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
} 